import {
  BONUS_SYMBOL_ID,
  COIN_SYMBOL_IDS,
  COLLECT_SYMBOL_ID,
  MULTIPLIER_SYMBOL_ID,
  SCATTER_1_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
} from "../../SymbolData";
import { readMysteryFeature } from "./mysteryStaging";

/**
 * Staging for the Golden Mystery (B2) feature — the BE calls it the coins game.
 *
 * ── How the feature is entered, and the only way it can be ──
 *
 * `storage.mystery_feature.replacement === "10"`. That is it, and the BE doc
 * says so in the same words as invariant I3: *"a mystery replaced with `bonus`
 * starts the Coins feature"*. B2 can never land directly, so the cells that ran
 * the reveal ARE the feature's cells. There is no separate coordinate list and
 * there must not be — the two would be free to disagree.
 *
 * ── What the server sends ──
 *
 * `storage.coins_feature`, an ARRAY OF ITERATIONS. Not an inference: the BE doc
 * states it, and gives the shape of every cell.
 *
 *   [
 *     [                          // iteration 1
 *       [[0, 0], "13", 1],       // coin        [coord, symbol, value (xBet)]
 *       [[0, 1], "15", 2],       // multiplier  [coord, symbol, value]
 *       [[0, 2], "14"],          // collect     [coord, symbol]
 *       [[0, 3], "11"],          // scatter     [coord, symbol]
 *       [[0, 4], "12", 8, 1]     // gamble      [coord, symbol, fs_count, fs_level]
 *     ],
 *     [ ... iteration 2 ]
 *   ]
 *
 * So the third element is NOT always a value. For the gamble scatter it is a
 * spin count with a level behind it, and for a collect or a plain scatter there
 * is no third element at all. Reading it as one field — which an earlier version
 * of this file did — turns a gamble scatter awarding 8 spins into a coin worth 8.
 *
 * ── What the FE does NOT have to work out ──
 *
 * The payout. It arrives in `wins` as `["coins", payout, coords]` and the doc is
 * explicit that it is final — *"already multiplied by every required multiplier,
 * including the freespins multiplier"*. So nothing here sums coins, applies a
 * multiplier or reads a level. The iterations are a SCRIPT for the animation,
 * and the money is already decided.
 *
 * Everything downstream asks this module rather than the payload, so a change of
 * shape lands in one place.
 */

const COIN_SYMBOL_ID = COIN_SYMBOL_IDS[0];

/** True when this round's mystery reveals into B2. */
export const isGoldenMysteryRound = (round) =>
  readMysteryFeature(round)?.replacementId === BONUS_SYMBOL_ID;

/**
 * One cell of one iteration, in the shape its symbol actually uses.
 *
 * @param   {Array} cell Raw `[coord, symbol, ...]` from the payload.
 * @returns {{reel:number,row:number,symbolId:number,value:number,
 *            fsCount:number|null,fsLevel:number|null}|null}
 */
const parseCell = (cell) => {
  if (!Array.isArray(cell)) return null;
  const [position, symbol, third, fourth] = cell;
  if (!Array.isArray(position)) return null;

  // Symbol ids and values arrive as STRINGS, per the BE doc — the same rule
  // mystery_feature.replacement follows. Everything downstream compares numbers.
  const symbolId = Number(symbol);
  if (!Number.isFinite(symbolId)) return null;

  const parsed = {
    reel: Number(position[0]),
    row: Number(position[1]),
    symbolId,
    // The number the cell CARRIES: a coin's value or a multiplier's factor.
    // Zero for the symbols that carry none, so a renderer can print it or not
    // without asking what kind of cell it is.
    value: 0,
    // Spins, for the two scatters that award them.
    fsCount: null,
    // The level the gamble scatter rolled. Only it has one.
    fsLevel: null,
  };

  if (symbolId === COIN_SYMBOL_ID || symbolId === MULTIPLIER_SYMBOL_ID) {
    parsed.value = Number(third) || 0;
  } else if (symbolId === SCATTER_2_SYMBOL_ID) {
    // [coord, "12", fs_count, fs_level]. It may award NOTHING — the doc says it
    // then re-rolls on each following iteration — so a zero count is a real
    // outcome and not a parse failure.
    parsed.fsCount = Number(third) || 0;
    parsed.fsLevel = Number(fourth) || 0;
  } else if (symbolId === SCATTER_3_SYMBOL_ID) {
    // [coord, "16", fs_count], fs_count in {1,2,3}. Free spins only.
    parsed.fsCount = Number(third) || 0;
  }
  // collect (14) and the plain scatter (11) carry nothing at all.

  return parsed;
};

/**
 * The feature's iterations, each a list of cells.
 *
 * @param   {object} round
 * @returns {Array<Array<object>>} Empty when this round has no feature.
 */
export const goldenMysteryIterations = (round) => {
  const raw = round?.storage?.coins_feature;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((iteration) =>
      (Array.isArray(iteration) ? iteration : []).map(parseCell).filter(Boolean),
    )
    .filter((iteration) => iteration.length > 0);
};

/**
 * What ONE cell dropped in the FIRST iteration.
 *
 * The first iteration is what the per-cell respin stops on. Everything after it
 * is the multiplier and collect loop rearranging the board, which is a separate
 * presentation with its own rules — multiplier before collect, collect once at
 * the end of its own iteration, and neither of them multiplying each other.
 *
 * @param   {object} round
 * @param   {number} reel
 * @param   {number} row
 * @returns {object|null} The parsed cell.
 */
export const goldenOutcomeAt = (round, reel, row) => {
  const [first] = goldenMysteryIterations(round);
  if (!first) return null;
  return first.find((c) => c.reel === reel && c.row === row) || null;
};

/**
 * The order the tracks STOP in, as an ordinal per cell.
 *
 * Every Golden cell starts spinning together and they stop one after another,
 * left to right and top to bottom: reel 0's rows first, then reel 1's, and so
 * on. With ten cells on two reels that reads as a wave crossing the board, and
 * it is the only thing that makes a big Golden Mystery feel deliberate instead
 * of simultaneous.
 *
 * Read off the MYSTERY coords rather than coins_feature, and that is deliberate:
 * invariant I2 says every mystery in one event reveals the same symbol, so in a
 * B2 round every mystery cell is a Golden cell. Taking the order from the same
 * list the reveal used means the two can never disagree about which cells exist.
 *
 * @param   {object} round
 * @returns {Map<string, number>} "reel,row" -> 0-based ordinal.
 */
export const goldenStopOrder = (round) => {
  const order = new Map();
  const coords = readMysteryFeature(round)?.coords;
  if (!Array.isArray(coords)) return order;

  // Sorted rather than trusted: `coords` arrives in whatever order the backend
  // wrote it, and the wave has to follow the board, not the payload.
  [...coords]
    .filter((c) => Array.isArray(c))
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .forEach(([reel, row], index) => order.set(`${reel},${row}`, index));

  return order;
};


/**
 * The feature, resolved into a SCRIPT the animation can play.
 *
 * ── Why this exists as a pure function ──
 *
 * `coins_feature` is a list of what DROPPED, not of what the board looked like.
 * Round 1431382 proves it: iteration 2 is `[[3,4],"14"]` and nothing else, and
 * iteration 3 is `[[3,5],"13",5]` and nothing else. Cells that did not change are
 * never restated. So the board has to be carried forward and each iteration
 * applied as a PATCH — and the numbers on screen (a multiplied coin, a
 * collector's total) exist nowhere in the payload at all.
 *
 * Working that out while animating it would mix two hard things. This does the
 * arithmetic alone, with no PIXI and no timing, and hands the animation a list of
 * states to move between.
 *
 * ── The rules it applies, and where each comes from ──
 *
 *   multiplier first, always                     invariant I4, and proven by
 *                                                round 1431382's payout
 *   it hits every coin AND every collector       I5 / BE doc
 *   multipliers do not multiply each other       BE doc
 *   it then deletes itself and its cell respins  I5, and the capture: the x3's
 *                                                cell is the collect next
 *   at most ONE collect per iteration            BE doc
 *   the collect sums coins and other collectors  I6 / BE doc
 *   it takes that total as its own value         I6
 *   the coins are removed, their cells respin    I6
 *   THE COLLECT ITSELF STAYS                     I6, stated twice
 *
 * ── `cellLimit`: the player may never read a number bigger than the payout ──
 *
 * GDD §18.2 step 5, on the max win: *"Counter STOPS as soon as it reaches the
 * Max win."* The collector is a counter — it is the one cell that accumulates —
 * and on a capped round it was landing on the UNCLAMPED sum while the round paid
 * the cap. Vladimir, 2026-09-23, on a round that showed ~35036x and paid 30000x:
 * *"player should not see more than he would get... its bad."*
 *
 * So a collector's total is clamped to this figure, in the same CELL units the
 * board is written in (xBet, before the level multiplier). The caller works it
 * out from the money the server actually paid — see goldenCellLimit — so an
 * ordinary round computes a limit it never reaches and nothing changes.
 *
 * Left out by `verifyGoldenScript` on purpose. That check exists to test our
 * READING of the server's script against the server's own payout, so it has to
 * see the script unclamped or it would agree with itself.
 *
 * @param   {object} round
 * @param   {object} [options]
 * @param   {number|null} [options.cellLimit] Ceiling for a collector's total.
 * @returns {{steps: Array, finalBoard: Map, finalTotal: number}}
 */
export const goldenMysteryScript = (round, { cellLimit = null } = {}) => {
  const capped = (value) =>
    typeof cellLimit === "number" && cellLimit >= 0
      ? Math.min(value, cellLimit)
      : value;
  const iterations = goldenMysteryIterations(round);
  const board = new Map();
  const steps = [];

  const at = (cell) => `${cell.reel},${cell.row}`;
  const coinsOn = (b) =>
    [...b.entries()].filter(([, c]) => c.symbolId === COIN_SYMBOL_ID);
  const collectorsOn = (b) =>
    [...b.entries()].filter(([, c]) => c.symbolId === COLLECT_SYMBOL_ID);

  iterations.forEach((drops, index) => {
    // 1. The patch lands.
    for (const cell of drops) {
      board.set(at(cell), { ...cell });
    }
    const afterDrops = new Map([...board].map(([k, v]) => [k, { ...v }]));

    // 2. Multipliers, all of them, before anything else.
    //
    // Every coin and every collector is multiplied; the multipliers themselves
    // are not, so the factors are read BEFORE any of them is applied. Two x2s
    // therefore make a coin x4 and leave each other alone.
    const multipliers = [...board.entries()]
      .filter(([, c]) => c.symbolId === MULTIPLIER_SYMBOL_ID)
      .map(([key, c]) => ({ key, value: c.value }));

    const factor = multipliers.reduce((f, m) => f * (m.value || 1), 1);
    if (multipliers.length) {
      for (const [key, cell] of board) {
        const isCoin = cell.symbolId === COIN_SYMBOL_ID;
        const isCollector = cell.symbolId === COLLECT_SYMBOL_ID;
        if (isCoin || isCollector) {
          // ── The coin is clamped too, the same as the magnet ──
          //
          // A x10 landing on a collector already at the ceiling would carry it
          // straight back over, so the clamp has to survive this phase. The
          // coin was left alone on the argument that a coin can never overreach
          // — and it can: a x5000 coin under a x10 reads 50,000, past the
          // 30,000x max win. Vladimir, 2026-09-24: *"we dont have fifty
          // thousand, we have max of 30 000 so thats what we have to show."*
          //
          // The same ceiling as the magnet — what the round paid, which on a
          // capped round IS the max win. An ordinary round never reaches it,
          // so nothing changes there.
          const raw = (cell.value || 0) * factor;
          board.set(key, { ...cell, value: capped(raw) });
        }
      }
      // Spent. Their cells are empty and respin in the next iteration.
      for (const { key } of multipliers) board.delete(key);
    }
    const afterMultiplier = new Map([...board].map(([k, v]) => [k, { ...v }]));

    // 3. The collect — ONCE, in the iteration it dropped, after the multipliers.
    //
    // "Once, at the end of the iteration in which it dropped" (BE doc). It is
    // never removed, so it stays on the board for the rest of the feature — but
    // it does NOT collect again. Reading it as "collect whenever a collector is
    // on the board" makes round 1431382 pay 50 instead of 80: the collector
    // swallows the iteration-3 coin that should have been left standing beside
    // it.
    //
    // So the test is whether it is in THIS iteration's drops, not whether it is
    // on the board.
    const droppedKeys = new Set(drops.map(at));
    const [collector] = collectorsOn(board).filter(([key]) =>
      droppedKeys.has(key),
    );
    let collect = null;
    if (collector) {
      const [collectKey] = collector;
      const coins = coinsOn(board);
      const others = collectorsOn(board).filter(([key]) => key !== collectKey);

      const total = capped(
        coins.reduce((t, [, c]) => t + (c.value || 0), 0) +
          others.reduce((t, [, c]) => t + (c.value || 0), 0),
      );

      board.set(collectKey, { ...board.get(collectKey), value: total });

      // ── ANSWERED by round 1472920, 2026-09-22 ──
      //
      // A swallowed collector KEEPS what it holds. It is not emptied.
      //
      // This block used to zero them, on the assumption that leaving the value
      // in place would count the same money twice. That was a guess — neither
      // document says — made when no capture had ever carried two collectors,
      // and the self-check below was put here to catch it. It did.
      //
      // Round 1472920 has THREE magnets and paid 9510 at bet 10, so the board
      // must end on 951:
      //
      //   i=0  [4,2] takes 36                      others: —
      //   i=1  [3,2] takes 83 + 36        = 119    [4,2] keeps 36
      //   i=2  [4,3] takes 61 + 119 + 36  = 216    [3,2] keeps 119, [4,2] 36
      //   i=3  a x2 doubles every one of them
      //
      //   coins 208 + 1  +  magnets 432 + 238 + 72  =  951   ✓  x 10 = 9510
      //
      // Zeroing them gives 569, which is 3820 short: the counter said 9510 and
      // the board the player could add up said 5690. So the double count is
      // real and deliberate — each magnet is a RUNNING TOTAL that includes the
      // ones before it, and every one of them pays.
      //
      // The screen already worked this way. The runner empties `swallowed` and
      // nothing else, so the other magnets were left holding their numbers on
      // screen while this script called them zero — which then painted them
      // blank and parked them as non-paying. Two of the four dark cells in the
      // 2026-09-22 report were these.

      // The coins are gone; their cells respin.
      for (const [key] of coins) board.delete(key);

      collect = {
        key: collectKey,
        reel: board.get(collectKey).reel,
        row: board.get(collectKey).row,
        total,
        swallowed: coins.map(([key]) => key),
        // The other magnets this one counted. They are NOT emptied — see
        // above — so this names what was ABSORBED, not what was cleared.
        absorbed: others.map(([key]) => key),
      };
    }

    // 4. What is empty now is what drops next.
    const cleared = [
      ...multipliers.map(({ key }) => key),
      ...(collect ? collect.swallowed : []),
    ];

    // ── EVERY scatter in this patch, whichever kind ──
    //
    // PLURAL, and that is the fix. This was `drops.find(...)` and named only
    // the first, so a collector that emptied five cells and refilled three of
    // them with `+FS` opened ONE window and left the other two shut — while
    // goldenPlusCells still counted all three spins. Vladimir, 2026-09-22:
    // "none of them opened after collector and none of them played spin... we
    // have a bug in free spins with multiple levels of golden scatters."
    //
    // Two different things arrive this way and both were invisible before:
    //
    //   SC1 (12) RE-ROLLING. A blank gamble scatter inside a base-game feature
    //   "re-rolls on each following iteration" (BE doc, recorded in
    //   docs/api-contract-mad-labs-freespins.md). The server says so by listing
    //   the same coordinate again with a new fs_count.
    //
    //   +FS (16) DROPPING. A cell the collector emptied respins, and what lands
    //   in it can be a +FS scatter. Its spins are counted — goldenPlusCells
    //   flattens every iteration — but nothing opened its window, so the player
    //   was paid for a cell they watched stay shut. Vladimir, 2026-09-22.
    //
    // Only one of the two can ever appear in a given game: `bs` replaces SC1 in
    // the bonus (invariant I14). So this names whichever it is and the caller
    // decides what to do with it — re-spin a window that is already open, or
    // open one that never was.
    //
    // EMPTY on every iteration that dropped neither, which is also the whole
    // answer when a gamble scatter awarded first time: a package is terminal.
    //
    // SC1 can only ever be one cell — there is a single gamble scatter — so in
    // the base game this list is 0 or 1 long and nothing about it changes. Only
    // the bonus can fill it, because only `+FS` arrives several at a time.
    const scatterDrops = drops.filter(
      (c) =>
        c.symbolId === SCATTER_2_SYMBOL_ID ||
        c.symbolId === SCATTER_3_SYMBOL_ID,
    );

    steps.push({
      iteration: index,
      drops,
      scatterDrops,
      afterDrops,
      multipliers,
      factor: multipliers.length ? factor : 1,
      afterMultiplier,
      collect,
      afterCollect: new Map([...board].map(([k, v]) => [k, { ...v }])),
      cleared,
    });
  });

  const finalTotal = [...board.values()].reduce(
    (t, c) =>
      c.symbolId === COIN_SYMBOL_ID || c.symbolId === COLLECT_SYMBOL_ID
        ? t + (c.value || 0)
        : t,
    0,
  );

  return { steps, finalBoard: board, finalTotal };
};

/**
 * Check the script against the money the server actually paid.
 *
 * The payout is final and arrives in `wins` as `["coins", payout, coords]`, so
 * the client never has to compute it — which means we can use it as a TEST
 * instead. `finalTotal x bet` must equal it.
 *
 * This is the only way we will learn the answer to the open question above: the
 * first time a round carries two collectors and our reading of them is wrong,
 * this disagrees and says so, in the wild, with the payload in hand.
 *
 * ── The level multiplier belongs in this sum ──
 *
 * In the bonus the server folds it in before sending: capture 1431382 paid 510,
 * which is sum 17 x bet 10 x the LEVEL-3 multiplier. `coins_feature` carries the
 * raw values, so a free-spins round checked at x1 disagrees with the payout by
 * exactly the level — a real warning about nothing. The caller passes what the
 * round was played at; the base game has no levels and leaves it at 1.
 *
 * @param   {object} round
 * @param   {number} [levelMultiplier] The bonus level's multiplier, x1 in base.
 * @returns {{ok:boolean, expected:number, actual:number}|null} null when the
 *          round has no feature.
 */
export const verifyGoldenScript = (round, levelMultiplier = 1) => {
  const { steps, finalTotal } = goldenMysteryScript(round);
  if (!steps.length) return null;

  const coinsWin = (round?.wins || []).find(
    (w) => w?.isFeaturePayout || (Array.isArray(w) && w[0] === "coins"),
  );
  const actual = Array.isArray(coinsWin) ? coinsWin[1] : coinsWin?.win;
  if (typeof actual !== "number") return null;

  const bet = round?.bet || 0;
  const level = levelMultiplier || 1;
  const expected = finalTotal * bet * level;
  const ok = expected === actual;

  if (!ok) {
    console.warn(
      "[GoldenMystery] the script does not add up to what was paid.",
      { expected, actual, bet, finalTotal, level, iterations: steps.length },
      "One of the loop's rules is wrong — the payload is the evidence.",
    );
  }
  return { ok, expected, actual };
};

/**
 * The ceiling a collector's total may show, in CELL units.
 *
 * ── Why this is read off the payout and not off the cap ──
 *
 * The cap is the ROUND's (bet x 30000) and this is ONE spin, so subtracting a
 * running total would mean tracking it. The server has already done that
 * arithmetic for us: on a capped response `outcome.win` is the clamped figure
 * (299990 on round 1473622, against a `wins` entry of 350360) because it
 * clamps the spin so the round lands exactly on the cap. So "what this spin
 * paid" IS the headroom, and asking for it needs no cap knowledge at all.
 *
 * `round.win`, NOT the `coins` entry in `wins` — that one carries the UNCLAMPED
 * feature payout and would compute a limit the board can never reach. Using the
 * spin total also keeps this safe when a round has line wins beside the feature:
 * it can only ever be too generous, never too strict, so an ordinary round is
 * guaranteed to compute a limit it never meets and nothing is clamped.
 *
 * ── The level is divided out ──
 *
 * The board is written in raw xBet units and goldenFinalBoard scales them by the
 * level on the way out, so the number the player READS is `value x level`. The
 * ceiling has to be in the same units the values are stored in.
 *
 * @param   {object} round
 * @param   {number} [levelMultiplier] The bonus level's multiplier, x1 in base.
 * @returns {number|null} null when it cannot be known, which clamps nothing.
 */
export const goldenCellLimit = (round, levelMultiplier = 1) => {
  const paid = round?.win;
  const bet = round?.bet || 0;
  const level = levelMultiplier || 1;
  // ── A win of ZERO is "unknown", never "clamp everything to nothing" ──
  //
  // A round with money on its board always paid something, so 0 here does
  // not describe the board: it is a round whose `win` was zeroed after the
  // fact — entering the room (setFreeSpinsActive) and the next spin's pending
  // both do it, and the settled board behind the room keeps its storage. With
  // 0 as a limit every magnet total and every multiplied coin printed blank
  // when the room handed back. Vladimir, 2026-09-24: *"what we have on board
  // when it finishes sequence, THATS WHAT WE TRANSFER."*
  if (typeof paid !== "number" || paid <= 0 || bet <= 0 || level <= 0) return null;
  return paid / (bet * level);
};

/**
 * The board as the feature LEAVES it — what each cell finally holds.
 *
 * Not the first iteration. A cell that started as a x2 ends as whatever respun
 * into it after the multiplier deleted itself, and a coin that was multiplied
 * and then collected ends as something else again. The picture the cell keeps —
 * the one that rides the reel out on the next spin — has to be the LAST thing
 * that landed there, not the first.
 *
 * Values included: a collector's total and a coin's multiplied value both live
 * here and nowhere in the payload.
 *
 * ── The level multiplier, when the caller asks for it ──
 *
 * GDD §7: in the bonus the Level multiplier is applied to the payout BEFORE it
 * is paid, and the server has already done so (see verifyGoldenScript). So the
 * numbers the cells are left holding must carry it too, or the board says one
 * thing and the counter says another. Applied to money cells only — a scatter's
 * value is a spin count, not an amount.
 *
 * @param   {object} round
 * @param   {number} [levelMultiplier] The bonus level's multiplier, x1 in base.
 * @returns {Map<string,{reel:number,row:number,symbolId:number,value:number}>}
 *          keyed "reel,row".
 */
export const goldenFinalBoard = (round, levelMultiplier = 1) => {
  const board = goldenMysteryScript(round, {
    cellLimit: goldenCellLimit(round, levelMultiplier),
  }).finalBoard;
  const level = levelMultiplier || 1;
  if (level === 1) return board;

  const scaled = new Map();
  for (const [key, cell] of board) {
    const money =
      cell.symbolId === COIN_SYMBOL_ID || cell.symbolId === COLLECT_SYMBOL_ID;
    scaled.set(
      key,
      money ? { ...cell, value: (cell.value || 0) * level } : { ...cell },
    );
  }
  return scaled;
};

/**
 * The board as the player last SAW it — goldenFinalBoard plus the cells the
 * LAST step emptied.
 *
 * goldenFinalBoard is the feature's arithmetic: a spent multiplier and a coin
 * the magnet took are deleted, because their cells respin. On every step but
 * the last they do, and the refill is on the board. After the last step
 * nothing respins, so those cells stand as the runner left them — the coin
 * emptied to `value: 0`, the multiplier still wearing its skin — and are simply
 * missing from the final board. Every paint that read it skipped them, so the
 * settled board and the room's arrival board showed the reel's own symbol there
 * instead of what the feature ended on.
 *
 * Vladimir, 2026-09-24: *"whatever the golden mystery ends, we picture that,
 * and once we press spin, THOSE SYMBOLS GO DOWN."*
 *
 * For PAINTING only. Money, the paying set and the level volley stay on
 * goldenFinalBoard: an emptied cell pays nothing.
 *
 * @param   {object} round
 * @param   {number} [levelMultiplier] The bonus level's multiplier, x1 in base.
 * @returns {Map<string,{reel:number,row:number,symbolId:number,value:number}>}
 */
export const goldenShownBoard = (round, levelMultiplier = 1) => {
  const board = new Map(goldenFinalBoard(round, levelMultiplier));
  const { steps } = goldenMysteryScript(round);
  const last = steps[steps.length - 1];
  if (!last) return board;

  // What goldenMysteryRunner does to them, and nothing more: a spent multiplier
  // is never repainted, a swallowed coin is set to an empty coin.
  for (const { key } of last.multipliers) {
    const landed = last.afterDrops.get(key);
    if (landed && !board.has(key)) board.set(key, { ...landed });
  }
  for (const key of last.collect?.swallowed || []) {
    const landed = last.afterDrops.get(key);
    if (landed && !board.has(key)) {
      board.set(key, { ...landed, symbolId: COIN_SYMBOL_ID, value: 0 });
    }
  }
  return board;
};

/**
 * The cells that actually PAY, as a set of "reel,row" keys.
 *
 * The server pays the feature with `["coins", payout, coords]`, and `coords` is
 * the feature's own cells — ALL of them. So a round whose board ends with a
 * scatter, a gamble scatter or a spent multiplier standing on it hands those
 * coordinates to the win presentation, which lights every one of them: the
 * whole board celebrates, including the cells that contributed nothing.
 *
 * Money is the test, and the final board is the only place that knows it. A
 * coin's value is its own; a collector's is the total it swallowed, which
 * exists nowhere in the payload. Both are computed by goldenMysteryScript.
 * Everything else on the board — SC (11), SC1 (12), a multiplier that has
 * already applied itself — pays nothing and must stay a dimmed texture.
 *
 * `value > 0` rather than the symbol alone: a collector that gathered nothing
 * is as empty as a blank scatter, and lighting it would say otherwise.
 *
 * Derived at USE time, never persisted. `wins` is saved with the round, so a
 * filter applied before saving would freeze into old records exactly as
 * `gambleScatter` did — see the note in useWinController.
 *
 * @param   {object} round
 * @returns {Set<string>} "reel,row" for every cell that pays.
 */
export const goldenPayingCells = (round) => {
  const paying = new Set();
  for (const [key, cell] of goldenFinalBoard(round)) {
    const money =
      cell.symbolId === COIN_SYMBOL_ID || cell.symbolId === COLLECT_SYMBOL_ID;
    if (money && (cell.value || 0) > 0) paying.add(key);
  }
  return paying;
};

/**
 * The +FS scatters the feature dropped, and the spins each one awards.
 *
 * One entry per DROP, not per board state: an iteration lists what landed, and a
 * scatter lands once and then stands there. Summing the board instead would pay
 * the same scatter again on every later iteration.
 *
 * Free spins only — `bs` (16) replaces SC1 in the bonus (invariant I14) and can
 * never drop in a base-game feature.
 *
 * @param   {object} round
 * @returns {Array<{reel:number,row:number,fsCount:number}>}
 */
export const goldenPlusCells = (round) => {
  // ── One entry per CELL, not per listing ──
  //
  // Flattening the iterations is right — a `+FS` that lands on a respin is in
  // a later patch and must be counted — but a cell that appears TWICE is one
  // scatter, not two. The base game's blank gamble scatter is re-listed on
  // every following iteration by design; a `+FS` awards, so by the same rule
  // it should never be, but "should" is the server's behaviour and not ours to
  // rely on.
  //
  // Nothing refills a `+FS` cell either — it is not money and not a
  // multiplier, so the loop never clears it — which is what makes one cell one
  // scatter, and a repeat a repeat.
  //
  // The last listing wins, so a cell the server revised reports its final
  // count rather than its first. Double-announcing is all this can cost today:
  // the awarded TOTAL comes from the server, and these drive the "+N free
  // spins" card only. That is still the player being told the wrong number.
  const byCell = new Map();
  for (const cell of goldenMysteryIterations(round).flat()) {
    if (cell?.symbolId !== SCATTER_3_SYMBOL_ID) continue;
    if (!((cell.fsCount || 0) > 0)) continue;
    byCell.set(`${cell.reel},${cell.row}`, cell);
  }
  return [...byCell.values()];
};

/**
 * The GAMBLE SCATTER the feature dropped, if it dropped one.
 *
 * SC1 (12) inside the Golden Mystery is the same award as SC1 on the board: a
 * package of spins and a starting level, which GDD §7 hands to the Gamble
 * Round — its resolution loop ends on "Gamble Scatter present? -> start Gamble
 * Round". The cell carries the whole package itself, `[coord, "12", fs_count,
 * fs_level]`, so nothing here has to be inferred from `features`.
 *
 * BASE GAME ONLY, and that is the BE doc's rule rather than a guess: SC1 drops
 * "only in the main game (and in a coins game started from the main game)", and
 * GDD §7 says the bonus replaces SC/SC1 with the additional-FS scatters. Inside
 * the room the spin-giving cell is `PLUS` — see goldenPlusCells.
 *
 * `fsCount > 0` is the filter, not `symbolId` alone: the doc says the scatter
 * may award NOTHING and re-roll on the next iteration, so a zero count is a
 * real outcome that must not arm a round.
 *
 * ONE, not a list. Two gamble scatters in one feature would be two packages for
 * one round and the server has no shape for that, so the FIRST is the award and
 * a second is ignored rather than silently overwriting it.
 *
 * @param   {object} round
 * @returns {{reel:number,row:number,fsCount:number,fsLevel:number}|null}
 */
export const goldenGambleCell = (round) =>
  goldenMysteryIterations(round)
    .flat()
    .find(
      (c) => c?.symbolId === SCATTER_2_SYMBOL_ID && (c.fsCount || 0) > 0,
    ) ?? null;

/**
 * The SC1 the feature dropped — AWARDED OR NOT.
 *
 * The presentation half of the pair above, and the distinction is GDD §8: SC1
 * "reveals its outcome and, unless BLANK, hands off to the Gamble Round". The
 * reveal always runs; only the hand-off is conditional. So the SHOW must find a
 * zero-count scatter and the AWARD must not — one filter cannot do both jobs,
 * and using goldenGambleCell for both is why a blank feature scatter used to
 * play nothing at all: no award meant no position, no position meant no reveal,
 * and the player watched a gamble scatter land and then simply sit there.
 *
 * Observed live on round 1451830 (2026-09-04): a board SC, a second SC from the
 * feature, and then SC1 from the feature — three scatters, and the third one
 * silent.
 *
 * ONE, for the same reason as the award: two gamble scatters in one round is a
 * shape the server has no answer for, so the first wins.
 *
 * @param   {object} round
 * @returns {{reel:number,row:number,fsCount:number,fsLevel:number}|null}
 */
export const goldenGambleRevealCell = (round) =>
  goldenMysteryIterations(round)
    .flat()
    .find((c) => c?.symbolId === SCATTER_2_SYMBOL_ID) ?? null;

/**
 * Every scatter the feature dropped that charges the LEVEL — one point each.
 *
 * Two symbols do it, and this is why the free-spins strip pool carries both:
 * `PLUS` (16) awards spins AND a point, and the plain SC (11) awards only the
 * point. GDD §3.3 states the bonus-game rule for SC outright — "each one
 * advances the Level track by 1 point" — and the same list drives the board
 * scatters (LEVEL_CHARGING_SYMBOL_IDS).
 *
 * Round 1434212 is the proof: `collected` went 8 -> 12 on a spin whose board
 * carried ONE scatter and whose feature dropped two `PLUS` cells. Three points
 * for four counted, and the fourth was an SC inside the feature.
 *
 * @param   {object} round
 * @returns {Array<{reel:number,row:number,symbolId:number,fsCount:number|null}>}
 */
export const goldenLevelCells = (round) =>
  goldenMysteryIterations(round)
    .flat()
    .filter(
      (c) =>
        c?.symbolId === SCATTER_1_SYMBOL_ID ||
        c?.symbolId === SCATTER_3_SYMBOL_ID,
    );

/**
 * The plain SC (11) cells the feature dropped.
 *
 * They count toward the free-spins trigger exactly as a board SC does — GDD §8
 * asks for two SC and one SC1, and says nothing about where they came from.
 * The board cannot answer for these: a B2 cell keeps symbol 10 whatever stands
 * inside it, so a scatter the feature dropped is invisible to a screen scan.
 *
 * Every DROP, not every board state — a scatter lands once and then stands
 * there, so summing the board would count the same one on every later
 * iteration.
 *
 * Base game only in practice: `bs` (16) replaces SC in the bonus room
 * (invariant I14), and that has its own list in goldenPlusCells.
 *
 * @param   {object} round
 * @returns {Array<{reel:number,row:number}>}
 */
export const goldenPlainScatterCells = (round) =>
  goldenMysteryIterations(round)
    .flat()
    .filter((c) => c?.symbolId === SCATTER_1_SYMBOL_ID);

/** Cells that carry a coin value, across every iteration. Reporting only. */
export const goldenCoinCells = (round) =>
  goldenMysteryIterations(round)
    .flat()
    .filter((c) => c.symbolId === COIN_SYMBOL_ID);

export default isGoldenMysteryRound;
